import Preloader from '../../../../../ui/svg/common/preloader';
import React, { useEffect, useState } from 'react';
import {
    couponActions,
    newCreditCardActions,
    rootActions,
    savedCreditCardActions,
    shippingAddressActions,
    useAppDispatch,
    useAppSelector
} from '../../data/store';
import {
    checkIsIllugenExtraCreditsSubscription,
    checkIsMasteringSubscription,
    checkIsSampleMakerSubscriptions,
    checkIsSubscription,
    checkIsVoiceRegenExtraCreditsSubscription,
    checkIsVoiceRegenSubscriptions,
    getConfirmButtonText,
} from '../../domain/shopping-cart-provider';
import { PaymentTabsEnum } from '../../enums/payment-tabs-enum';
import { scrollToPath, validateBillingAddress, validateShippingAddress } from '../../domain/shopping-cart-validations';
import { validateControl } from '../../../../../domain/validation/validation';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import { TaxIdStatusEnum } from '../../enums/tax-id-status-enum';
import { ADDRESS_NOT_VALID_EVENT, sendCartEvent } from '../../domain/shopping-cart-events-provider';
import { CreditCardTabTabsEnum } from '../../enums/credit-card-tab';
import { getAddressIDs } from '../../domain/shopping-cart-address-provider';
import { getUTMFromStorage } from '../../../../../domain/marketing/utm/utm-provider';
import { CartDataValidationCode, ICartData, IConfirmOrderRequest, IConfirmOrderResponse, } from '../../interfaces';
import { confirmOrder } from '../../data/data-service';
import { USA_ID } from '../../../../../domain/addresses/addresses-provider';
import { IResponse } from '../../../../../interfaces/common-interfaces';
import { DEFAULT_ERROR } from '../../../../../DAL/data-provider';
import { ConfirmOrderResultErrorCodeEnum } from '../../enums/confirm-order/confirm-order-result-error-enum';
import { CartSteps } from '../../enums/cart-steps';
import { InsertOrderErrorCodeEnum } from '../../enums/confirm-order/insert-order-error-enum';
import { CompleteOrderResultErrorCodeEnum } from '../../enums/confirm-order/complete-order-result-error-code-enum';
import { removeCartDataStorage } from '../../data/cart-storage-provider';
import { sha256 } from '../../../../../domain/security/security-provider';
import {
    handleCardHolderNameValidation,
    handleNewCreditCardValidation,
    handleSavedCreditCardsValidation, redirectToThankYouPage
} from '../../../cart-shared-bridger-pay/domain/bridger-pay-provider';
import { outbrainPurchaseEvent } from '../../../../../domain/marketing/outbrain-provider';
import { sendApplePayEventToGA } from '../../../cart-shared-bridger-pay/domain/apple-pay-provider';

export enum ErrorMode {
    Error = 0,
    Warning = 1,
}

export const CONFIRM_ORDER_BTN_STOP_LOADING_EVENT = 'CONFIRM_ORDER_BTN_STOP_LOADING_EVENT';

const ConfirmOrderBtn = () => {

    const dispatch = useAppDispatch();

    const [ loading, setLoading ] = useState(false);
    const [ serverError, setServerError ] = useState('');
    const [ errorMode, setErrorMode ] = useState<ErrorMode>(ErrorMode.Error);

    const cartData = useAppSelector(store => store.root.cartData);
    const isSbsAgreeCheckboxChecked = useAppSelector(store => store.root.isSbsAgreeCheckboxChecked);
    const couponControl = useAppSelector(store => store.coupons.couponControl);

    // used for payment tabs validation
    const selectedPaymentTab = useAppSelector(store => store.root.selectedPaymentTab);
    const selectedCreditCardTab = useAppSelector(store => store.root.selectedCreditCardTab);

    // new and saved credit cards
    const bloxController = useAppSelector(store => store.newCreditCard.bloxController);
    const nameOnCardControl = useAppSelector(store => store.newCreditCard.nameOnCardControl);
    const cvvControl = useAppSelector(store => store.savedCreditCard.cvvControl);
    const savedCreditCardToken = useAppSelector(store => store.savedCreditCard.savedCreditCardToken);
    const savedCreditCardChecksum = useAppSelector(store => store.savedCreditCard.savedCreditCardChecksum);
    const savedCreditCardEncryptedCvv= useAppSelector(store => store.savedCreditCard.savedCreditCardEncryptedCvv);
    const savedCreditCardKenticoItemID = useAppSelector(store => store.savedCreditCard.savedCreditCardKenticoItemID);
    const rememberMe = useAppSelector(store => store.newCreditCard.rememberMe);

    // billing form controls
    const countries = useAppSelector(store => store.root.countries);
    const billToControl = useAppSelector(store => store.billingAddress.billToControl);
    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const billingAddressLine2Control = useAppSelector(store => store.billingAddress.addressLine2Control);
    const billingCityControl = useAppSelector(store => store.billingAddress.cityControl);
    const billingZipCodeControl = useAppSelector(store => store.billingAddress.zipCodeControl);
    const billingCountryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const billingStateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const billingPhoneControl = useAppSelector(store => store.billingAddress.phoneControl);
    const billingPhoneCountryIdControl = useAppSelector(store => store.billingAddress.phoneCountryIdControl);

    // shipping form controls
    const isShippingDifferentFromBilling = useAppSelector(store => store.shippingAddress.isShippingDifferentFromBilling);
    const shipToControl = useAppSelector(store => store.shippingAddress.shipToControl);
    const shippingAddressLine1Control = useAppSelector(store => store.shippingAddress.addressLine1Control);
    const shippingAddressLine2Control = useAppSelector(store => store.shippingAddress.addressLine2Control);
    const shippingCityControl = useAppSelector(store => store.shippingAddress.cityControl);
    const shippingZipCodeControl = useAppSelector(store => store.shippingAddress.zipCodeControl);
    const shippingCountryIdControl = useAppSelector(store => store.shippingAddress.countryIdControl);
    const shippingStateIdControl = useAppSelector(store => store.shippingAddress.stateIdControl);
    const shippingPhoneControl = useAppSelector(store => store.shippingAddress.phoneControl);
    const shippingPhoneCountryIdControl = useAppSelector(store => store.shippingAddress.phoneCountryIdControl);
    const upsShippingCodeControl = useAppSelector(store => store.shippingAddress.upsShippingCodeControl);

    // https://wavesaudio.atlassian.net/browse/DSWEB-6
    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);
    const isUSA = billingCountryIdControl && billingCountryIdControl.value.toString() === USA_ID.toString();

    const isInAppPurchase = useAppSelector(store => store.root.isInAppPurchase);
    const isSubscription = checkIsSubscription(cartData);

    // refer a friend credit
    const isReferCreditChecked = useAppSelector(store => store.root.isReferCreditChecked);
    const referCreditControl = useAppSelector(store => store.root.referCreditControl);
    const isReferRecurringChecked = useAppSelector(store => store.root.isReferRecurringChecked);

    const isZeroPurchase = cartData.priceData.totalPrice < 1 && !checkIsSubscription(cartData);
    const isNewCreditCard = selectedPaymentTab === PaymentTabsEnum.CreditCard &&
                                     selectedCreditCardTab === CreditCardTabTabsEnum.NewCreditCard;
    const isApplePayEnabled = useAppSelector(store => store.root.isApplePayEnabled);
    const isApplePay = !isZeroPurchase && selectedPaymentTab === PaymentTabsEnum.ApplePay;

    useEffect(() => {

        const stopLoading = (evt: CustomEvent) => {
            const error = evt?.detail?.error || '';
            setLoading(false);

            if(error) {
                setServerError(error);
            }
        };

        // @ts-ignore
        document.addEventListener(CONFIRM_ORDER_BTN_STOP_LOADING_EVENT, stopLoading);

        return () => {
            // @ts-ignore
            document.removeEventListener(CONFIRM_ORDER_BTN_STOP_LOADING_EVENT, stopLoading);
        };

    }, [ ]);

    // ------------------------- VALIDATIONS ------------------------------

    /**
     * If coupon text box has a value, and coupon was not submitted,
     * show a validation error.
     */
    const handleCouponValidation = () => {
        const isValid = couponControl.value.trim() === '';

        if(!isValid) {
            dispatch(
                couponActions.main({
                    showCouponNavError: true,
                })
            );
        }

        return isValid;
    };

    /**
     * If my rewards checkbox is checked, and the value was not submitted,
     * show a validation error.
     */
    const handleMyRewardsValidation = () => {
        const isValid = !(isReferCreditChecked && cartData.priceData.selectedUserCredit <= 0);

        if(!isValid) {
            dispatch(
                rootActions.main({
                    showCreditNavError: true,
                })
            );
        }

        return isValid;
    };

    /**
     * If credit is enabled & opened,
     * and the text box has a value that is not submitted ---->
     * show a validation error.
     */
    const handleCreditValidation = () => {
        const isInvalid = cartData.isReferFriendEnabled && // setting in kentico
            isReferCreditChecked && // credit checkbox is checked
            cartData.priceData.selectedUserCredit <= 0 && // credit form is visible (not applied)
            referCreditControl.value.trim(); // credit has some value

        if(isInvalid) {
            dispatch(
                rootActions.main({
                    showCreditNavError: true,
                })
            );
        }

        return !isInvalid;
    };

    const handleBillingAddressValidation = () => {
        let isValid = true;

        // validate billing address --------------------------
        const billingFormFields: [IFormControl, string][] = [

            [billToControl, 'billToControl'],
            [billingAddressLine1Control, 'addressLine1Control'],
            [billingAddressLine2Control, 'addressLine2Control'],
            [billingCityControl, 'cityControl'],
            [billingZipCodeControl, 'zipCodeControl'],
            [billingCountryIdControl, 'countryIdControl'],
            [billingStateIdControl, 'stateIdControl'],
            [billingPhoneControl, 'phoneControl'],
            [billingPhoneCountryIdControl, 'phoneCountryIdControl'],

            // https://wavesaudio.atlassian.net/browse/DSWEB-6
            [taxIdSelectedStatusControl, 'taxIdSelectedStatusControl']
        ];

        // https://wavesaudio.atlassian.net/browse/DSWEB-2247
        if(taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES){
            billingFormFields.push([taxIdControl, 'taxIdControl']);
        }

        const isBillingFormValid = validateBillingAddress(billingFormFields, dispatch);

        if(!isBillingFormValid){
            isValid = false;

            // open 'the edit address form' if not opened yet
            sendCartEvent(ADDRESS_NOT_VALID_EVENT);
        }

        return isValid;
    };

    const handleShippingAddressValidation = () => {
        if(!cartData.isShippingNeeded || !isShippingDifferentFromBilling) return true;

        let isValid = true;

        const shippingFormFields: [IFormControl, string][] = [

            [shipToControl, 'shipToControl'],
            [shippingAddressLine1Control, 'addressLine1Control'],
            [shippingAddressLine2Control, 'addressLine2Control'],
            [shippingCityControl, 'cityControl'],
            [shippingZipCodeControl, 'zipCodeControl'],
            [shippingCountryIdControl, 'countryIdControl'],
            [shippingStateIdControl, 'stateIdControl'],
            [shippingPhoneControl, 'phoneControl'],
            [shippingPhoneCountryIdControl, 'phoneCountryIdControl'],

            // https://wavesaudio.atlassian.net/browse/DSWEB-6
            [taxIdControl, 'taxIdControl'],
            [taxIdSelectedStatusControl, 'taxIdSelectedStatusControl']
        ];

        const isShippingFormValid = validateShippingAddress(shippingFormFields, dispatch);
        if (!isShippingFormValid) {
            isValid = false;

            // open 'the edit address form' if not opened yet
            sendCartEvent(ADDRESS_NOT_VALID_EVENT);
        }

        return isValid;
    };

    const handleUpsShipping = () => {
        if(!cartData.isShippingNeeded) return true;

        // validate ups shipping
        let isValid = validateShippingAddress([
            [upsShippingCodeControl, 'upsShippingCodeControl']
        ], dispatch);
        if(!isValid){

            dispatch(
                shippingAddressActions.main({
                    upsShippingError: 'Please select shipping option.',
                })
            );

            // scroll to 'ups-shipping-code' section
            scrollToPath('#ups-shipping-code');
        }

        return isValid;
    };

    // ------------------------- SERVER RESPONSE ------------------------------

    const handleConfirmOrderResponse = (
        confirmOrderResponse: IResponse,
        setServerError: Function,
        setCartData: Function,
        isInAppPurchase: boolean,
        dispatch: Function,
    ) => {
        let isResponseValid = confirmOrderResponse && confirmOrderResponse.statusCode === 200;

        if(confirmOrderResponse?.data?.relatedData?.savedCreditCards) {
            dispatch(
                savedCreditCardActions.main({
                    savedCreditCards: confirmOrderResponse?.data?.relatedData?.savedCreditCards || [],
                })
            );
        }

        const hasModelValidationError =
            isResponseValid &&
            confirmOrderResponse &&
            confirmOrderResponse.data &&
            confirmOrderResponse.data.errors &&
            confirmOrderResponse.data.errors.length > 0;

        // show the first error from .net model state validation
        if(hasModelValidationError){
            // @ts-ignore - I check if errors list exists above
            setServerError(confirmOrderResponse.data.errors[0]);
            return false;
        }

        // something went wrong - there is no content in the response
        if(!confirmOrderResponse.data || !confirmOrderResponse.data.relatedData){
            setServerError(DEFAULT_ERROR);
            return false;
        }

        // make sure we take 'data.isValid' into account
        if(isResponseValid){
            isResponseValid = confirmOrderResponse.data.isValid;
        }

        const result = confirmOrderResponse.data.relatedData as IConfirmOrderResponse;
        
        // make sure we take 'result.isValid' into account
        if(isResponseValid){
            isResponseValid = result.isValid;
        }

        // handle error message from BrigerPay that went through custom table filter
        if(!isResponseValid && result.zoozErrorMessage){
            setServerError(result.zoozErrorMessage);
            return false;
        }

        // general error messages from server, like zip code for USA, etc.
        if(!isResponseValid && result.errorMessage){
            setServerError(result.errorMessage);
            return false;
        }

        // here it's possible to check each of the errors by the status codes
        // result.errorCode, result.insertOrderError, result.completeOrderError
        // but most of such errors can't be shown to user ---> just general default message should be shown

        // the 'total has changed' case
        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.TotalPriceChanged){
            setServerError('Your shopping cart total has changed since you last reviewed it.');
            setErrorMode(ErrorMode.Warning);

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        // the 'total has changed' case
        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.TotalPriceChangedFreeTrialAbuse){
            setServerError('Your shopping cart total has been updated because you have already used your Waves subscription free trial.');
            setErrorMode(ErrorMode.Warning);

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.WavesUpdatePlanError){
            window.location.href = '/account/get-waves-update-plan';
            return true;
        }

        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.UpgradeError){
            window.location.href = '/account/upgrades';
            return true;
        }

        // https://wavesaudio.atlassian.net/browse/DSWEB-2331
        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.NoActiveItems){
            if(result.cartData){
                setCartData(result.cartData);
            }

            dispatch(
                rootActions.main({
                    cartStep: CartSteps.First,
                })
            );
            return false;
        }

        // the 'is fraud' case
        if(result.insertOrderError === InsertOrderErrorCodeEnum.IsFraudError ||
            result.insertOrderError === InsertOrderErrorCodeEnum.FraudError101 ||
            result.insertOrderError === InsertOrderErrorCodeEnum.FraudError102){
            setServerError('<p>You may not continue with your purchase due to too many attempts. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.FraudError103){
            setServerError('<p>You may not continue with your purchase as your credit card was flagged as potential fraud. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.FraudErrorDisposableEmail){
            setServerError('<p>Your purchase cannot be completed because the provided username or email address is temporary or disposable.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.FraudError200){
            setServerError('<p>You may not continue with your purchase as your account was flagged as potential fraud. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.completeOrderError === CompleteOrderResultErrorCodeEnum.InsertSubscriptionOrderError){
            removeCartDataStorage();

            // redirect: account has been charged
            window.location.href = isInAppPurchase ? '/inapp/cart/error?errorId=97' : '/cart/error?errorId=97';
            return true;
        }

        if(checkIsIllugenExtraCreditsSubscription(cartData) &&
            result.insertOrderError === InsertOrderErrorCodeEnum.CreditPackError){
            setServerError('<p>ILLUGEN credit packs can be purchased only when you have an <a href="/illugen">ILLUGEN subscription</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(checkIsVoiceRegenExtraCreditsSubscription(cartData) &&
            result.insertOrderError === InsertOrderErrorCodeEnum.CreditPackError){
            setServerError('<p>Voice ReGen credit packs can be purchased only when you have a <a href="/voice-regen/pricing">Voice ReGen subscription</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.IsEligibleWCSSBSError){
            setServerError('<p>You may not purchase this Subscription.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        // https://wavesaudio.atlassian.net/browse/DSWEB-3965
        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.StateDoesntMatchZipCode){
            setServerError('The state and zip code you’ve provided seem to be inconsistent. Please check and try again.');
            return false;
        }

        // Apply pay cashier token / launcher ----------------------
        let cashierToken = '';
        let cashierKey = '';

        if(isApplePay) {
            if(!isApplePayEnabled) {
                console.log('Apple pay is selected but not enabled.');
                setServerError(DEFAULT_ERROR);
                return false;
            }

            cashierKey = (result.applyPayCashierKey ?? '').trim();
            cashierToken = (result.applyPayCashierToken ?? '').trim();

            if(!cashierKey || !cashierToken){
                console.log('Apple pay cashier token or cashier key is empty.');
                setServerError(DEFAULT_ERROR);
                return false;
            }
        }

        // if we arrived here, and the response is not valid ---> there is an unknown error
        if(!isResponseValid){
            setServerError(DEFAULT_ERROR);
            return false;
        }

        // all is well, the purchase went OK -----------------------

        // Send marketing purchase events.
        outbrainPurchaseEvent();
        // window.wBraze?.logOrderPlacedEventFromCartData(result.orderGUID, result.cartData);

        /*if(checkIsVoiceRegenNotFreeSubscriptionOrCredits(cartData)) {
            sendTiktokPlaceAnOrder('Place an Order VRG Paid', cartData);
        }*/

        // in case of PayPal or AliPay ---> redirect
        if(result.redirect){
            window.location.href = result.redirect;
            return true;
        }

        // in case of ApplyPay ---> make additional ajax request
        if(isApplePay) {
            dispatch(
                rootActions.main({
                    applePayPopup: {
                        cashierKey,
                        cashierToken,
                        orderGUID: result?.orderGUID ?? '',
                        isInAppPurchase,
                    },
                })
            );
            return true; // don't hide preloader
        }

        redirectToThankYouPage(result?.orderGUID ?? '', isInAppPurchase);

        return true;
    };

    // ------------------------- PROCEED TO CHECKOUT ------------------------------

    const proceedHandlerHelper = async () : Promise<boolean> => {

        let _creditCardToken = '';
        let _encryptedCvv = '';
        let _cvv = '';
        let _savedCreditCardKenticoItemID = 0;
        let _cardChecksum = '';

        let isValid = handleCouponValidation();
        if(!isValid){
            console.log('Coupon validation error.');
            return false;
        }

        isValid = handleMyRewardsValidation();
        if(!isValid){
            console.log('My rewards validation error.');
            return false;
        }

        isValid = handleCreditValidation();
        if(!isValid){
            console.log('Credit validation error.');
            return false;
        }

        if(isApplePay && !isApplePayEnabled) {
            console.log('Apple pay is selected but not enabled.');
            return false;
        }

        isValid = handleCardHolderNameValidation(
            selectedPaymentTab,
            selectedCreditCardTab,
            isZeroPurchase,
            nameOnCardControl,
            (updatedControl: IFormControl) => {
                dispatch(
                    newCreditCardActions.setControl({
                        controlName: 'nameOnCardControl',
                        control: validateControl(updatedControl)
                    })
                );
            }
        );
        if(!isValid){
            console.log('Card holder name validation error.');
            return false;
        }

        const savedCreditCardsRes = handleSavedCreditCardsValidation(
            selectedPaymentTab,
            selectedCreditCardTab,
            isZeroPurchase,
            savedCreditCardToken,
            savedCreditCardEncryptedCvv,
            savedCreditCardKenticoItemID,
            savedCreditCardChecksum,
            cvvControl,
            (updatedControl: IFormControl) => {
                dispatch(
                    savedCreditCardActions.setControl({
                        controlName: 'cvvControl',
                        control: validateControl(updatedControl),
                    })
                );
            }
        );
        if(!savedCreditCardsRes.isValid){
            console.log('Saved credit card validation error.');
            return false;
        }

        if(savedCreditCardsRes.savedCreditCardToken) _creditCardToken = savedCreditCardsRes.savedCreditCardToken;
        if(savedCreditCardsRes.encryptedCvv) _encryptedCvv = savedCreditCardsRes.encryptedCvv;
        if(savedCreditCardsRes.cvv) _cvv = savedCreditCardsRes.cvv;
        if(savedCreditCardsRes.savedCreditCardKenticoItemID) _savedCreditCardKenticoItemID = savedCreditCardsRes.savedCreditCardKenticoItemID;
        if(savedCreditCardsRes.cardChecksum) _cardChecksum = savedCreditCardsRes.cardChecksum;

        const newCreditCardsRes = await handleNewCreditCardValidation(
            selectedPaymentTab,
            selectedCreditCardTab,
            isZeroPurchase,
            isSubscription,
            bloxController,
            rememberMe,
            nameOnCardControl.value,
            cvvControl.value.trim(),
            (error: string) => setServerError(error),
            (token: string) => {
                dispatch(
                    newCreditCardActions.main({
                        newCreditCardToken: token,
                    })
                );
            }
        );
        if(!newCreditCardsRes.isValid){
            if(newCreditCardsRes.serverError) {
                setServerError(newCreditCardsRes.serverError || DEFAULT_ERROR);
            }
            console.log('New credit card validation error.');
            return false;
        }

        if(newCreditCardsRes.newCreditCardToken) _creditCardToken = newCreditCardsRes.newCreditCardToken;
        if(newCreditCardsRes.encryptedCvv) _encryptedCvv = newCreditCardsRes.encryptedCvv;
        if(newCreditCardsRes.cvv) _cvv = newCreditCardsRes.cvv;
        if(newCreditCardsRes.cardChecksum) _cardChecksum = newCreditCardsRes.cardChecksum;

        isValid = handleBillingAddressValidation();
        if(!isValid){
            console.log('Billing address validation error.');
            return false;
        }

        isValid = handleShippingAddressValidation();
        if(!isValid){
            console.log('Shipping address validation error.');
            return false;
        }

        isValid = handleUpsShipping();
        if(!isValid){
            console.log('UPS validation error.');
            return false;
        }

        const addressIDs = getAddressIDs();

        // don't pass a state id to the server, if the country doesn't have states (for example, old values from
        // session storage), because it may fail in validation.
        const billingSelectedCountry = countries.find(country => country.countryID.toString() === billingCountryIdControl.value.toString());
        const billingStateID = billingSelectedCountry &&
        billingSelectedCountry.states &&
        billingSelectedCountry.states.length > 0 ? Number(billingStateIdControl.value) || 0 : 0;

        // https://dev13.waves.com/cart?utm_source=aaa&utm_medium=bbb&utm_campaign=ddd
        const utm = getUTMFromStorage();

        const __cvv = _cvv ? await sha256(_cvv) : _cvv;

        const confirmOrderModel: IConfirmOrderRequest = {
            paymentMethodToken: _creditCardToken,
            encryptedCvv: _encryptedCvv,
            cvv: __cvv ?? '',
            savedCreditCardKenticoItemID: _savedCreditCardKenticoItemID,
            paymentType: selectedPaymentTab,
            cardChecksum: _cardChecksum,

            // new credit card parameters
            rememberMe,
            isNewCreditCard,

            // used for 'total changed' validation error
            checksumTotal: cartData.priceData.totalPrice,

            billingAddress: {
                addressID: addressIDs.billingAddressID,
                addressPersonalName: billToControl.value,
                addressLine1: billingAddressLine1Control.value,
                addressLine2: billingAddressLine2Control.value,
                addressCountryID: Number(billingCountryIdControl.value) || 0,
                addressStateID: billingStateID,
                addressCity: billingCityControl.value,
                addressZip: billingZipCodeControl.value,
                phoneNumber: billingPhoneControl.value,
                phoneCountryID: Number( billingPhoneCountryIdControl.value) || 0,

                taxID: isUSA ? '' : taxIdControl.value || '',
                taxIDSelectedStatus: isUSA ? TaxIdStatusEnum.NONE : (Number(taxIdSelectedStatusControl.value) || TaxIdStatusEnum.NONE),
            },

            isShippingDifferentFromBilling: cartData.isShippingNeeded && isShippingDifferentFromBilling,

            lastUTMSource: utm ? utm.lastSource : '',
            lastUTMMedium: utm ? utm.lastMedium : '',
            lastUTMCampaign: utm ? utm.lastCampaign : '',

            useCreditRecurring: isReferRecurringChecked,
        };

        if(cartData.isShippingNeeded && isShippingDifferentFromBilling){

            // don't pass a state id to the server, if the country doesn't have states (for example, old values from
            // session storage), because it may fail in validation.
            const shippingSelectedCountry = countries.find(country => country.countryID.toString() === shippingCountryIdControl.value.toString());
            const shippingStateID = shippingSelectedCountry &&
            shippingSelectedCountry.states &&
            shippingSelectedCountry.states.length > 0 ? Number(shippingStateIdControl.value) || 0 : 0;

            confirmOrderModel.shippingAddress = {
                addressID: addressIDs.shippingAddressID,
                addressPersonalName: shipToControl.value,
                addressLine1: shippingAddressLine1Control.value,
                addressLine2: shippingAddressLine2Control.value,
                addressCountryID: Number(shippingCountryIdControl.value) || 0,
                addressStateID: shippingStateID,
                addressCity: shippingCityControl.value,
                addressZip: shippingZipCodeControl.value,
                phoneNumber: shippingPhoneControl.value,
                phoneCountryID: Number( shippingPhoneCountryIdControl.value) || 0,
            };
        }

        if(cartData.isShippingNeeded){
            confirmOrderModel.upsShippingCode = upsShippingCodeControl.value;
        }

        setServerError('');
        setErrorMode(ErrorMode.Error);

        const response = await confirmOrder(confirmOrderModel);

        if(isApplePay) {
            sendApplePayEventToGA('ApplePay Confirm Order', response?.data?.relatedData?.orderGUID);
        }

        // Server returns different status codes;
        // Each status code or error code may cause its own behaviour.
        const hasRedirect = handleConfirmOrderResponse(
            response,
            setServerError,
            (updatedCartData: ICartData) => {
                dispatch(
                    rootActions.setCartData(updatedCartData)
                );
            },
            isInAppPurchase,
            dispatch
        );

        if(!hasRedirect){
            setLoading(false);
        }

        return true;
    };

    const proceedHandler = async () => {
        const _confirmButtonDisabled = isConfirmButtonDisabled();
        if(_confirmButtonDisabled) {
            if(
                (
                    checkIsSampleMakerSubscriptions(cartData) ||
                    checkIsMasteringSubscription(cartData) ||
                    checkIsVoiceRegenSubscriptions(cartData)
                )
                &&
                (
                    cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError ||
                    cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError
                )
            ) return;

            setServerError('Please agree to the terms.');
            return;
        }

        setLoading(true);
        const isValid = await proceedHandlerHelper();

        if(!isValid) {
            setLoading(false);
        }
    };

    /**
     * https://wavesaudio.atlassian.net/browse/DSWEB-4470 ----------------------------------------
     */
    const onCreditCheckboxChange = () => {
        setServerError('');

        dispatch(
            rootActions.main({
                isSbsAgreeCheckboxChecked: !isSbsAgreeCheckboxChecked,
            })
        );
    };

    const isConfirmButtonDisabled = () : boolean => {

        // https://wavesaudio.atlassian.net/browse/DSWEB-4475
        if(
            (
                checkIsSampleMakerSubscriptions(cartData) ||
                checkIsMasteringSubscription(cartData) ||
                checkIsVoiceRegenSubscriptions(cartData)
            )
            &&
            (
                cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError ||
                cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError
            )
        ) return true;

        if(!isSubscription) return false;

        return !isSbsAgreeCheckboxChecked;
    };

    const confirmButtonDisabled = isConfirmButtonDisabled();

    const confirmButonText = getConfirmButtonText(cartData);

    return (
        <>
            {
                isSubscription &&
                <label className="flex items-center mb-4">
                    <input
                        className="mr-2"
                        type="checkbox"
                        checked={ isSbsAgreeCheckboxChecked }
                        onChange={ onCreditCheckboxChange }
                    />
                    <span>I agree to the terms above</span>
                </label>
            }

            {
                !loading &&
                <button
                    type="button"
                    className={ `w-btn text-xl font-normal py-3 ${ confirmButtonDisabled ? 'bg-gray-400' : '' }` }
                    aria-disabled={ confirmButtonDisabled }
                    data-qa="cart-step3-confirm-order"
                    onClick={ proceedHandler }>
                    { confirmButonText }
                </button>
            }

            {
                loading &&
                <div className="flex justify-center my-6" data-qa="cart-step3-order-process-preloader">
                    <Preloader width="30px" height="30px" color="#4f4f4f"/>
                </div>
            }

            {
                serverError && <div
                    role="alert"
                    className={ `my-4 break-words ${ errorMode === ErrorMode.Warning ? 'info-box' : 'error-box' }` }
                    data-qa="cart-step3-confirm-order-error"
                    dangerouslySetInnerHTML={ {__html: serverError} }/>
            }

            {
                checkIsSampleMakerSubscriptions(cartData) &&
                <>
                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError) &&
                        <div
                            role="alert"
                            className="error-box mt-4">
                            <p>You are already subscribed to this plan. If you need more credits, you can <a
                                href="/illugen/pricing">subscribe to a bigger plan</a>, or <a href="/illugen/pricing#packs">purchase
                                standalone credit packs</a>.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError) &&
                        <div
                            role="alert"
                            className="error-box mt-4">
                            <p>You are already subscribed to this plan. If you need more credits, you can <a
                                href="/illugen/pricing#packs">purchase standalone credit packs</a>.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsUpgradeWarning) &&
                        <div
                            role="alert"
                            className="msg-box mt-4">
                            <p>You are upgrading your ILLUGEN subscription: the new plan will replace your current plan.
                                Your existing unused credits will be saved.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsBiggerPlanError) &&
                        <div
                            role="alert"
                            className="msg-box mt-4">
                            <p>You are downgrading your ILLUGEN subscription: the new plan will replace your current
                                plan. Your existing unused credits will be saved.</p>
                        </div>
                    }
                </>
            }

            {
                checkIsMasteringSubscription(cartData) &&
                <>
                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError) &&
                        <div
                            role="alert"
                            className="error-box mt-4">
                            <p>You are already subscribed to this plan. If you need more credits, you can <a
                                href="/online-mastering/pricing">subscribe to a bigger plan</a>, or <a href="/online-mastering/pricing#packs">purchase
                                standalone credit packs</a>.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError) &&
                        <div
                            role="alert"
                            className="error-box mt-4">
                            <p>You are already subscribed to this plan. If you need more credits, you can <a
                                href="/online-mastering/pricing#packs">purchase standalone credit packs</a>.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsUpgradeWarning) &&
                        <div
                            role="alert"
                            className="msg-box mt-4">
                            <p>You are upgrading your Online Mastering subscription: the new plan will replace your current plan.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsBiggerPlanError) &&
                        <div
                            role="alert"
                            className="msg-box mt-4">
                            <p>You are downgrading your Online Mastering subscription: the new plan will replace your current plan.</p>
                        </div>
                    }
                </>
            }

            {
                checkIsVoiceRegenSubscriptions(cartData) &&
                <>
                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError) &&
                        <div
                            role="alert"
                            className="error-box mt-4">
                            <p>You are already subscribed to this plan. If you need more credits, you can <a
                                href="/voice-regen/pricing">subscribe to a bigger plan</a>, or <a href="/voice-regen/pricing">purchase
                                standalone credit packs</a>.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError) &&
                        <div
                            role="alert"
                            className="error-box mt-4">
                            <p>You are already subscribed to this plan. If you need more credits, you can <a
                                href="/voice-regen/pricing">purchase standalone credit packs</a>.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsUpgradeWarning) &&
                        <div
                            role="alert"
                            className="msg-box mt-4">
                            <p>You are upgrading your Voice ReGen subscription: the new plan will replace your current plan. Your existing unused credits will be saved.</p>
                        </div>
                    }

                    {
                        (cartData?.validationCode === CartDataValidationCode.CreditsBiggerPlanError) &&
                        <div
                            role="alert"
                            className="msg-box mt-4">
                            <p>You are downgrading your Voice ReGen subscription: the new plan will replace your current plan. Your existing unused credits will be saved.</p>
                        </div>
                    }
                </>
            }

            {
                (cartData?.validationCode === CartDataValidationCode.AlreadySubscribedToWCA) &&
                <div
                    role="alert"
                    className="msg-box mt-4">
                    <p>You are already subscribed to Waves Creative Access. If you don’t need multiple subscriptions, consider upgrading or downgrading your current subscription by clicking the <a href="/account/products">Manage Subscription button</a>.</p>
                </div>
            }

        </>
    )
};

export default ConfirmOrderBtn;